import { Link } from "@tanstack/react-router";
import { Phone, Mail, MapPin } from "lucide-react";
import logo from "@/assets/apex-av-new-logo-optimized.webp";
import { Button } from "@/components/ui/button";
import { openCookieSettings } from "@/lib/advertising-consent";

export function SiteFooter() {
  return (
    <footer className="architectural-grid w-full border-t border-border bg-muted/30">
      <div className="container mx-auto grid gap-12 px-6 py-16 md:grid-cols-[1.2fr_1fr_1fr_1fr] md:py-20">
        <div>
          <div className="mb-5 flex items-center gap-3">
            <img
              src={logo}
              alt="Apex AV Denver smart home installation company logo"
              width={200}
              height={200}
              loading="lazy"
              decoding="async"
              className="h-12 w-auto shrink-0"
            />
            <span className="font-display text-xl font-semibold uppercase tracking-[0.16em]">
              Apex <span className="text-brand-text">AV</span>
            </span>
          </div>
          <p className="text-sm text-muted-foreground">
            Denver's premier home automation, audio, and video installation specialists. Licensed, insured, and locally
            owned.
          </p>
        </div>
        <div>
          <p className="eyebrow mb-5 text-brand-text">Services</p>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li>
              <Link to="/services/home-theater" className="hover:text-foreground">
                Custom Home Theater
              </Link>
            </li>
            <li>
              <Link to="/services/dolby-atmos-home-theater" className="hover:text-foreground">
                Dolby Atmos Home Theater
              </Link>
            </li>
            <li>
              <Link to="/services/smart-home" className="hover:text-foreground">
                Smart Home Automation
              </Link>
            </li>
            <li>
              <Link to="/services/control4" className="hover:text-foreground">
                Control4 Home Automation
              </Link>
            </li>
            <li>
              <Link to="/services/whole-home-audio" className="hover:text-foreground">
                Whole-Home Audio
              </Link>
            </li>
            <li>
              <Link to="/services/lighting" className="hover:text-foreground">
                Smart Lighting
              </Link>
            </li>
            <li>
              <Link to="/services/shading" className="hover:text-foreground">
                Motorized Shading
              </Link>
            </li>
            <li>
              <Link to="/services/outdoor" className="hover:text-foreground">
                Outdoor Entertainment
              </Link>
            </li>
            <li>
              <Link to="/services/security" className="hover:text-foreground">
                Security & Surveillance
              </Link>
            </li>
            <li>
              <Link to="/services/networking" className="hover:text-foreground">
                Networking & Wi-Fi
              </Link>
            </li>
            <li>
              <Link to="/services/av-installation" className="hover:text-foreground">
                AV Installation
              </Link>
            </li>
            <li>
              <Link to="/services/commercial-av" className="hover:text-foreground">
                Commercial AV
              </Link>
            </li>
            <li>
              <Link to="/services/whole-home-video" className="hover:text-foreground">
                Whole-Home Video
              </Link>
            </li>
            <li>
              <Link to="/services/voice-control" className="hover:text-foreground">
                Voice Control
              </Link>
            </li>
            <li>
              <Link to="/services/climate-control" className="hover:text-foreground">
                Climate Control
              </Link>
            </li>
            <li>

              <Link to="/services" className="hover:text-foreground font-semibold text-brand-text">
                All Services →
              </Link>
            </li>
          </ul>
        </div>
        <div>
          <p className="eyebrow mb-5 text-brand-text">Service Areas</p>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li><Link to="/service-areas/$slug" params={{ slug: "denver" }} className="hover:text-foreground">Denver</Link></li>
            <li><Link to="/service-areas/$slug" params={{ slug: "cherry-creek" }} className="hover:text-foreground">Cherry Creek</Link></li>
            <li><Link to="/service-areas/$slug" params={{ slug: "highlands-ranch" }} className="hover:text-foreground">Highlands Ranch</Link></li>
            <li><Link to="/service-areas/$slug" params={{ slug: "castle-rock" }} className="hover:text-foreground">Castle Rock</Link></li>
            <li><Link to="/service-areas/$slug" params={{ slug: "castle-pines" }} className="hover:text-foreground">Castle Pines</Link></li>
            <li><Link to="/service-areas/$slug" params={{ slug: "boulder" }} className="hover:text-foreground">Boulder</Link></li>
            <li><Link to="/service-areas/$slug" params={{ slug: "parker" }} className="hover:text-foreground">Parker</Link></li>
            <li><Link to="/service-areas/$slug" params={{ slug: "aurora" }} className="hover:text-foreground">Aurora</Link></li>
            <li><Link to="/service-areas/$slug" params={{ slug: "golden" }} className="hover:text-foreground">Golden</Link></li>

            <li><Link to="/service-areas" className="hover:text-foreground font-semibold text-brand-text">All Areas →</Link></li>
          </ul>
        </div>
        <div>
          <p className="eyebrow mb-5 text-brand-text">Contact</p>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-brand-text" />{" "}
              <a href="tel:+13033173233" className="hover:text-foreground">
                (303) 317-3233
              </a>
            </li>
            <li className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-brand-text" />{" "}
              <a href="mailto:info@apexav.com" className="hover:text-foreground">
                info@apexav.com
              </a>
            </li>
            <li className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-brand-text" /> Denver, CO
            </li>
            <li className="flex items-center gap-2 pt-1">
              <svg className="h-4 w-4 text-brand-text" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
              <a href="https://www.facebook.com/apexavdenver" target="_blank" rel="noopener noreferrer" className="hover:text-foreground">
                Facebook
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div className="mx-auto max-w-7xl border-t border-border px-6 py-7">
        <p className="eyebrow mb-4 text-brand-text">Popular in Denver</p>
        <ul className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted-foreground">
          <li>
            <Link to="/home-theater-installation-near-me" className="hover:text-foreground">
              Home Theater Installation Near Me
            </Link>
          </li>
          <li>
            <Link to="/whole-home-audio-installation-near-me" className="hover:text-foreground">
              Whole-Home Audio Installation Near Me
            </Link>
          </li>
          <li>
            <Link to="/services/dolby-atmos-home-theater" className="hover:text-foreground">
              Dolby Atmos Installation Denver
            </Link>
          </li>
          <li>
            <Link to="/home-theater-near-me" className="hover:text-foreground">
              Home Theater Near Me
            </Link>
          </li>
          <li>
            <Link to="/denver/smart-home-installation" className="hover:text-foreground">
              Denver Smart Home Installation
            </Link>
          </li>
          <li>
            <Link to="/highlands-ranch/home-theater-installation" className="hover:text-foreground">
              Highlands Ranch Home Theater
            </Link>
          </li>
          <li>
            <Link to="/cherry-creek/home-theater-installation" className="hover:text-foreground">
              Cherry Creek Home Theater
            </Link>
          </li>
          <li>
            <Link to="/castle-rock/home-theater-installation" className="hover:text-foreground">
              Castle Rock Home Theater
            </Link>
          </li>
          <li>
            <Link to="/castle-pines/smart-home-installation" className="hover:text-foreground">
              Castle Pines Smart Home
            </Link>
          </li>
          <li>
            <Link to="/blog/custom-home-theater-design-guide" className="hover:text-foreground">
              Home Theater Design Guide
            </Link>
          </li>
          <li>
            <Link to="/sitemap" className="hover:text-foreground">
              Site Map
            </Link>
          </li>
        </ul>
      </div>

      <div className="border-t border-border py-6 px-6 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-center text-xs text-muted-foreground">
        <span>© {new Date().getFullYear()} Apex AV. All rights reserved. Serving the Denver Metro Area.</span>
        <span className="hidden sm:inline">·</span>
        <Link to="/blog" className="hover:text-foreground">
          Blog
        </Link>
        <span className="hidden sm:inline">·</span>
        <Link to="/privacy" className="hover:text-foreground">
          Privacy Policy
        </Link>
        <span className="hidden sm:inline">·</span>
        <Link to="/cookies" className="hover:text-foreground">
          Cookie Policy
        </Link>
        <span className="hidden sm:inline">·</span>
        <Button variant="link" size="sm" className="h-auto p-0 text-xs text-muted-foreground" onClick={openCookieSettings}>
          Cookie settings
        </Button>
      </div>
    </footer>
  );
}
